import { useOne } from '@refinedev/core'
import { useQuery } from '@tanstack/react-query'

import { dealerBackofficeGetBuyerNewOrderGroup, type FulfillmentOrderRoot } from '@repo/sdk'

interface OrderDetailData {
  /** Representative fulfillment order (the group's first one in group mode). */
  order: FulfillmentOrderRoot | undefined
  /** Every fulfillment order of the buyer group; empty outside group mode. */
  groupOrders: FulfillmentOrderRoot[]
  isGroupMode: boolean
  isLoading: boolean
  isError: boolean
  refetch: () => void
}

/**
 * Loads an order detail either as a single fulfillment order or as a buyer's new-order group.
 *
 * Two rules keep the group view matching the row the dealer clicked:
 *
 * **The requested fulfillment order is the anchor.** It used to be `groupOrders[0]`, so a buyer
 * with more than one open parcel got the first one's address, amount and broadcast no matter which
 * row was opened.
 *
 * **The group is scoped to the anchor's delivery address.** The endpoint returns every open
 * fulfillment order for the buyer, but only same-address orders actually share a parcel — the
 * server refuses to ship a batch spanning two addresses, so summing across them showed a total the
 * dealer could never ship as one.
 *
 * A group also stops existing the moment its orders ship (the anchor drops out of the endpoint's
 * answer, which may even come back empty), so we fall back to the plain FO detail instead of
 * rendering `DetailNotFound` on a perfectly valid order.
 */
export function useOrderDetailData(
  id: string | undefined,
  buyerId: string | null,
): OrderDetailData {
  const buyerGroupQuery = useQuery({
    queryKey: ['dealer-buyer-new-orders', buyerId],
    enabled: !!buyerId,
    queryFn: async () => {
      const response = await dealerBackofficeGetBuyerNewOrderGroup({ path: { buyerId: buyerId! } })
      const responseData = response.data
      if (!responseData) throw new Error('Buyer new-order group not found')
      return responseData
    },
  })

  const buyerOpenOrders = buyerGroupQuery.data?.fulfillmentOrders ?? []
  const anchor = buyerOpenOrders.find((order) => order.fulfillmentOrderId === id)
  // Shipped, cancelled, or otherwise no longer a "new order" — show it on its own.
  const groupUnavailable = !!buyerId && buyerGroupQuery.isSuccess && !anchor
  const isGroupMode = !!buyerId && !groupUnavailable

  // The parcel is what the server says ships together (`combinedShippingGroupKey`: same address,
  // broadcast purchases only). Scoping by address alone pulled a marketplace order — which always
  // ships on its own — into a broadcast parcel. A server without the key falls back to the address.
  const anchorGroupKey: string | undefined = anchor?.combinedShippingGroupKey
  const anchorAddressId = anchor?.shippingAddress?.addressId ?? null
  const groupOrders = anchor
    ? buyerOpenOrders.filter((order) =>
        anchorGroupKey !== undefined
          ? order.combinedShippingGroupKey === anchorGroupKey
          : (order.shippingAddress?.addressId ?? null) === anchorAddressId,
      )
    : []

  const { query } = useOne<FulfillmentOrderRoot>({
    resource: 'order-list',
    id: id ?? '',
    queryOptions: { enabled: !!id && (!buyerId || groupUnavailable) },
  })

  return {
    order: isGroupMode ? anchor : query.data?.data,
    groupOrders: isGroupMode ? [...groupOrders] : [],
    isGroupMode,
    isLoading: buyerId
      ? buyerGroupQuery.isLoading || (groupUnavailable && query.isLoading)
      : query.isLoading,
    isError: buyerId
      ? buyerGroupQuery.isError || (groupUnavailable && query.isError)
      : query.isError,
    refetch: () => {
      if (buyerId && !groupUnavailable) {
        void buyerGroupQuery.refetch()
        return
      }
      void query.refetch()
    },
  }
}
